import React from "react";
import { css } from "@emotion/core";
import { Margin, Text, TypographyName, useTheme } from "@zapier/design-system";
import {
  Currency,
  defaultCurrency,
} from "@zapier/currency/esm/hooks/useCurrencies";
import {
  formatPriceToBeInCurrency,
  RoundingStrategy,
} from "@zapier/currency/esm/utils/formatPriceToBeInCurrency";

type FancyPriceProps = {
  /** Price in USD pennies (i.e. hundredths of a USD dollar) */
  children: number;
  /** Currency to show price in */
  currency?: Currency;
  /**
   * For currencies that don't have decimals/fractional units (e.g. JPY), what type of rounding strategy should we use?
   * For example:
   *   - When showing monthly prices for annual plans, we should round UP in favor of the user (price * 12 will be less than what the user actually pays)
   *   - When showing standard monthly prices, use standard rounding practices i.e. round UP for >= .50 and DOWN for < .50
   */
  roundingStrategy?: RoundingStrategy;
  /** Sets smaller typography taking less space as a whole */
  isSmall?: boolean;
};

/**
 * Returns the decimal separator for the user's locale. Examples:
 *   - Returns . for English
 *   - Returns , for Swedish
 */
const getDecimalSeparator = () => {
  const value = 1.1;
  return value.toLocaleString().substring(1, 2);
};

const root = css`
  display: flex;
  align-items: top;
  margin: 8px;
`;

// Let the pricing page choose its own padding/margins
const rebrandRootCss = css`
  margin: 0px;
`;

// Get typography used by elements based on isSmall prop flag
const getTypography = (isSmall = false): { [key: string]: TypographyName } => {
  return isSmall
    ? {
        currency: "smallPrint1Semibold",
        decimal: "smallPrint1Bold",
        price: "pageHeader7",
        symbol: "smallPrint1Semibold",
      }
    : {
        currency: "paragraph1",
        decimal: "paragraph1Bold",
        price: "pageHeader5",
        symbol: "sectionHeader3Semibold",
      };
};

/**
 * Displays a price in a styled and user-friendly format.
 */
export const FancyPrice = ({
  children,
  roundingStrategy,
  currency = defaultCurrency,
  isSmall = false,
}: FancyPriceProps) => {
  const { isRebrand } = useTheme();
  const price = formatPriceToBeInCurrency(children, currency, false, {
    maxDecimals: 2,
    minDecimals: 0,
    roundingStrategy: currency?.zeroDecimal ? roundingStrategy : undefined,
    useRelativeRounding: false,
  });
  const separator = getDecimalSeparator();
  const tokens = price.split(separator);

  if (isRebrand) {
    const typography = getTypography(isSmall);
    return (
      <div css={[root, rebrandRootCss]}>
        <Margin margin={isSmall ? "4px 0 0 0" : "5px 0 0 0"}>
          <Text color="neutral800" type={typography.symbol}>
            {currency.symbol}
          </Text>
        </Margin>

        <Text color="neutral800" type={typography.price}>
          {tokens[0]}
        </Text>

        {tokens.length > 1 && (
          <Margin margin={isSmall ? "4px 0 0 2px" : "1px 0 0 2px"}>
            <Text color="neutral800" type={typography.decimal}>
              {separator}
              {tokens[1]}
            </Text>
          </Margin>
        )}

        {currency.symbol === "$" && (
          <Margin margin={isSmall ? "4px 0 0 1px" : "1px 0 0 5px"}>
            <Text color="neutral800" type={typography.currency}>
              {currency.name}
            </Text>
          </Margin>
        )}
      </div>
    );
  }

  return (
    <div css={root}>
      <Margin margin="0 5px 0 0">
        <Text color="gray40" type="sectionHeader1">
          {currency.symbol}
        </Text>
      </Margin>

      <Text color="gray80" type="pageHeader5">
        {tokens[0]}
      </Text>

      {tokens.length > 1 && (
        <Margin margin="5px 0 0 2px">
          <Text color="gray80" type="paragraph3Bold">
            {separator}
            {tokens[1]}
          </Text>
        </Margin>
      )}

      {currency.symbol === "$" && (
        <Margin margin="5px 0 0 5px">
          <Text color="gray40" type="paragraph3Bold">
            {currency.name}
          </Text>
        </Margin>
      )}
    </div>
  );
};
